import jQuery from 'jquery';
import GoogleMapsLoader from 'google-maps';
import _ from 'underscore';

if (!EXTERNAL_JQUERY) {
  jQuery.noConflict(true);
}

/**
 *  LetMeMap class instances are exposed to the portal to create and manage widgets.
 *  @memberof core
 */
class LetMeMap {

  get _version() {
    console.log('The current widget version is: ' + VERSION);
  }

  /**
   * Creates an instance of LetMeMap.
   * @param {function} widgetClass The constructor function of the Widget subclass that will be created.
   */
  constructor(widgetClass) {
    if (!widgetClass) {
      throw new Error('The widget class parameter must exist');
    }
    this.widgetClass = widgetClass;
  }

  /**
   * @typedef {object} GoogleMapsLoadConfig
   * @desc Configuration related to the load of Google Maps API JS Library
   * @memberof core.Config
   * @see https://developers.google.com/maps/documentation/javascript/tutorial#Loading_the_Maps_API
   * @property {string} [LANGUAGE='en'] The language to load the GoogleMaps libary in
   * @property {string[]} [LIBRARIES=['places']] The libraries to load with Google Maps
   * @property {string} [VERSION='3'] The Google Maps JS API version to use
   * @property {string} [CLIENT=null] The Google Maps JS API client id to use to load the API. 
   * If not present the KEY property value will be used.
   * @property {string} [KEY=null] The Google Maps JS API key to be used to load the API. 
   * If not present and CLIENT is also not present an error will be thrown upon widget creation.
   */

  /**
   * @typedef {object} GoogleMapsOptions
   * @extends google.maps.MapOptions
   * @memberof core.Config
   * @see https://developers.google.com/maps/documentation/javascript/reference#MapOptions
   */

  /**
   * @typedef {object} ConfigBounds A bounding box JSON representation
   * @property {number} bounds.neLat bounding box's north east point latitude
   * @property {number} bounds.neLng bounding box's north east point longitude
   * @property {number} bounds.swLat bounding box's south west point latitude
   * @property {number} bounds.swLng bounding box's south west point longitude
   * @memberof core.Config
   */

  /**
   * @typedef {object} DefaultView
   * @property {core.Config.ConfigBounds} [bounds=null] The bounding box to which center the map's initial view
   * @property {boolean|core.Config.ConfigBounds} [limitBounds=null] The bounding box outside of which the map view cannot be set.
   * @property {number[]} [center=null] An array containing [latitude,longitude] values to be set as map center. 
   * Ignored if bounds exist. Overrides the  mapOptions.center property if set. {@see core.Config.GoogleMapsOptions}.
   * @property {number} [zoom=null] The initial zoom level of the map. 
   * Overrides mapOptions.zoom property if set. {@see core.Config.GoogleMapsOptions}.
   * @memberof core.Config
   */

  /**
   * @typedef {object} Breakpoints 
   * @property {string} [maxMobile=null] The max widget container width for being in mobile responsive mode 
   * @property {string} [maxTablet=null] The max widget container width for being in  tablet responsive mode 
   * @memberof core.Config 
   */

  /**
   * @typedef {object} Layer
   * @desc Configuration for a given layer. Configuration must include at least a "type" property.
   * Other properties existence depend on the particular core.config.BaseLayer extending class being used.
   * This configuration will be sent to layer classes' constructors as the source property.
   * 
   * @property {string} type The type of the layer to create as defined in the project's src/layers/index.js file 
   * @property {string} provider the registered name of the provider class that will be instantiated for the layer.
   * @memberof core.Config
   */

  /**
   * @typedef {object<string, core.Config.Layer>} Layers
   * @desc A definition of layers in which keys are layer identifiers and values are their configurations.
   * @memberof core.Config
   */

  /**
   * @typedef Component
   * @desc Component configuration.
   * 
   * @property {string} componentConstructorClass The registered class name for the component
   * @property {string} position The position of the component in the map or its parent component
   * @property {object<string,any>} properties Configuration properties for the component
   * @memberof core.Config
   */

  /**
   * @typedef {object<string, core.Config.Component>} Components
   * @memberof core.Config
   */

  /**
   * @class 
   * @name Config 
   * @classdesc Not an actual class but used to document the configuration for the Widgets created with LMM. 
   * Extend this type to add project related configuration.
   * @property {boolean} debugEnabled If the widget will be created in debug mode that easis debugging and testing
   * by e.g. unoptimizing the markers.
   * @property {boolean} doValidation If the widget and its components will perform validation of the configurations received. 
   * Set it to true when developing and testing.
   * @property {core.Config.GoogleMapsLoadConfig} gmapLoadConfig Google Maps library configuration
   * @property {core.Config.GoogleMapsOptions} mapOptions Google Maps initialization options 
   * @property {core.Config.DefaultView} defaultView The default view for the map
   * @property {core.Config.Breakpoints} breakpoints The responsive breakpoints for the widget
   * @property {core.Config.LayerDefaults} layerDefaults The default configuration for layers added to the widget.
   * @property {core.Config.Layers} propertyLayers The property layers configured for the widget,
   *  i.e. those showing elements related to the client's business such as hotels, houses etc.
   * @property {core.Config.Layers} layers The non-property layers configured for the widget
   * @property {core.Config.Components} components The components to be added to the map.
   * @memberof core
   */

  /**
   * Creates a map widget
   * @param {string} containerId - The id of the DOM element where the map will be created.
   * @param {core.Config} config - The config provided by json
   * @param {core.APIObject} api - The object that allows the widget to interact with the portal.
   * @example <caption>Example to create a new widget</caption>
   *  lmmMapsWidget.createWidget('map-container', config, apiObject)
   * @returns {Promise<core.widget.Widget>}
   */
  createWidget(containerId, config, api) {
    let deferred = jQuery.Deferred();
    let promise = deferred.promise();
    this.widgets = this.widgets || {};
    if (this.widgets[containerId]) {
      deferred.reject('Duplicated Widget');
    } else {
      this.widgets[containerId] = promise;
      this._gmapPromise(config.gmapLoadConfig)
        .done(() => {
          deferred.resolve(new this.widgetClass(containerId, config, api));
        })
        .fail((reason) => {
          deferred.reject(reason);
        });
    }
    return promise;
  }

  /**
   * Get the widget by id
   * @function
   * @param {string} containerId - The id of the DOM element where the widget is embedded.
   * @returns {core.widget.Widget}
   */
  getWidget(containerId) {
    return this.widgets[containerId];
  }

  /**
   * Destroys the widget instance linked to a container DOM element id.
   * 
   * @param {string} containerId The container ID that the widget to be removed is attached to.
   */
  destroyWidget(containerId) {
    let widget = this.widgets[containerId];
    widget.destroy();
    delete this.widgets[containerId];
  }

  _gmapPromise(config) {
    let deferred = jQuery.Deferred(),
      defaults = {};
    GoogleMapsLoader.release(() => {
      defaults = {
        LANGUAGE: 'en',
        SENSOR: false,
        LIBRARIES: ['places'],
        VERSION: '3'
      };
      // jQuery extend true parameter makes it a deep extend, which is useful for this.
      jQuery.extend(true, GoogleMapsLoader, defaults, config);

      if (_.isEmpty(config.CLIENT) && _.isEmpty(config.KEY)) {
        throw new Error('Google Map loaded without apikey or clientid.');
      }
      //clear before load
      GoogleMapsLoader.load(() => {
        deferred.resolve();
      });
    });
    return deferred.promise();
  }
}

export default LetMeMap;



// WEBPACK FOOTER //
// ./lmm-widget/src/LetMeMap.js